import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  coursesApi,
  type CEFRLevel,
  type Marathon,
  type MarathonListParams,
  type MarathonStatus,
} from '@/api/courses';
import { PageHeader } from '@/components/ui/PageHeader';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Pill } from '@/components/ui/Pill';
import { Input } from '@/components/ui/Input';
import { Pagination } from '@/components/ui/Pagination';
import { SearchInput } from '@/components/ui/SearchInput';
import { Select } from '@/components/ui/Select';
import { humanizeError } from '@/lib/errors';
import { Modal } from '@/components/ui/Modal';
import { MarathonFormModal } from './MarathonFormModal';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { ActionSheet } from '@/components/mobile/ActionSheet';
import { MPickerChip } from '@/components/mobile/MPickerChip';
import { MAdd, MBlock, MEmpty, MIcon, MSegmented, MTitle } from '@/components/mobile/MobileKit';
import { useIsMobile } from '@/hooks/useIsMobile';

const hasLevels = (a?: string, b?: string) => !!(a?.trim()) || !!(b?.trim());

export function AdminMarathonsPage() {
  const ask = useConfirm();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [filters, setFilters] = useState<MarathonListParams>({ offset: 0, limit: 20 });
  const [creatingOpen, setCreatingOpen] = useState(false);

  /**
   * Что показываем: рабочие марафоны или образцы.
   *
   * Образец — полноценный марафон, из которого рабочие создаются копией. Своего
   * раздела в меню ему не нужно: он ЖИВЁТ здесь же, просто в другой вкладке.
   * Отдельный пункт «Материалы» столкнулся бы с уже существующим — там видео и
   * статьи для учеников.
   */
  const [tab, setTab] = useState<'courses' | 'templates'>('courses');
  const [fromTemplate, setFromTemplate] = useState<Marathon | null>(null);
  /** Курс, который привязываем к образцу. */
  const [bindFor, setBindFor] = useState<Marathon | null>(null);
  /** Телефон: курс, чей лист «⋯» открыт. */
  const [sheetFor, setSheetFor] = useState<Marathon | null>(null);
  const isMobile = useIsMobile();

  const STATUS_FILTER_OPTIONS = [
    { value: '', label: t('marathon.filterAllStatuses') },
    { value: 'draft', label: t('marathon.filterDrafts') },
    { value: 'published', label: t('marathon.filterPublished') },
    { value: 'archived', label: t('marathon.filterArchived') },
  ];

  const STATUS_LABELS: Record<string, string> = {
    draft: t('marathon.statusDraft'),
    published: t('marathon.statusPublished'),
    archived: t('marathon.statusArchived'),
  };
  const ACCESS_LABELS: Record<string, string> = {
    open: t('marathon.accessOpen'),
    sequential: t('marathon.accessSequential'),
    with_review: t('marathon.accessWithReview'),
  };
  const LANGUAGE_LABELS: Record<string, string> = {
    EN: t('marathon.langEN'),
    RU: t('marathon.langRU'),
    KZ: t('marathon.langKZ'),
  };

  const statusLabel = (s?: string) => STATUS_LABELS[(s ?? '').toLowerCase()] ?? s ?? '—';
  const accessLabel = (a?: string) => ACCESS_LABELS[(a ?? '').toLowerCase()] ?? a ?? '—';
  const languageLabel = (l?: string) => LANGUAGE_LABELS[(l ?? '').toUpperCase()] ?? l ?? '—';

  // Отбор делает сервер. Отбирать в браузере нельзя: страница отдаёт по 20
  // записей, и образец, попавший на вторую страницу, во вкладке не появился бы
  // вовсе — а счётчик и постраничка считали бы вперемешку марафоны с образцами.
  const query: MarathonListParams = { ...filters, template: tab === 'templates' ? 'true' : 'false' };

  const list = useQuery({
    queryKey: ['admin', 'marathons', query],
    queryFn: () => coursesApi.listAllMarathons(query),
  });

  const shown = list.data?.items ?? [];

  /**
   * Снятие образца с марафона.
   *
   * Образец появляется в СОСЕДНЕЙ вкладке, а список перед глазами не меняется —
   * без сообщения нажатие выглядело бы как «ничего не произошло». Поэтому после
   * успеха говорим, что образец создан и где он.
   */
  const makeTemplate = useMutation({
    mutationFn: (id: string) => coursesApi.makeTemplate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'marathons'] });
      void ask({ title: t('adminMarathons.makeTemplateTitle'), message: t('adminMarathons.makeTemplateDone'), notice: true });
    },
  });
  const unmakeTemplate = useMutation({
    mutationFn: (id: string) => coursesApi.unmakeTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'marathons'] }),
  });

  /**
   * Раскатка правок образца по созданным из него марафонам.
   *
   * Отчёт показываем целиком, включая «оставлено как есть»: методист должен
   * видеть, что часть мест куратор правил и туда правки не пошли, — иначе он
   * будет считать, что образец разошёлся весь, и не поймёт расхождения.
   */
  const syncTemplate = useMutation({
    mutationFn: (id: string) => coursesApi.syncFromTemplate(id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['admin', 'marathons'] });
      void ask({
        title: t('adminMarathons.syncDoneTitle'),
        message:
          r.courses === 0
            ? t('adminMarathons.syncNoCourses')
            : t('adminMarathons.syncReport', {
                courses: r.courses,
                updated: r.updated,
                kept: r.kept,
                added: r.added,
                hidden: r.hidden,
              }),
        notice: true,
      });
    },
  });

  const duplicate = useMutation({
    mutationFn: (id: string) => coursesApi.duplicateMarathon(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'marathons'] }),
  });

  const publish = useMutation({
    mutationFn: (id: string) => coursesApi.publishMarathon(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'marathons'] }),
  });

  const archive = useMutation({
    mutationFn: (id: string) => coursesApi.archiveMarathon(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'marathons'] }),
  });

  const actionError =
    duplicate.error ||
    publish.error ||
    archive.error ||
    makeTemplate.error ||
    unmakeTemplate.error ||
    syncTemplate.error;

  // Подтверждения — одни на компьютер и телефон: спросить на одном и не спросить на другом нельзя.
  // Спрашиваем: образец пропадает из списка марафонов и перестаёт быть доступным ученикам.
  const askMakeTemplate = async (m: Marathon) => {
    if (
      await ask({
        title: t('adminMarathons.makeTemplateTitle'),
        message: t('adminMarathons.makeTemplateBody'),
        confirmLabel: t('adminMarathons.makeTemplateConfirm'),
      })
    ) {
      makeTemplate.mutate(m.id);
    }
  };
  const askSync = async (m: Marathon) => {
    if (
      await ask({
        title: t('adminMarathons.syncTitle'),
        message: t('adminMarathons.syncBody'),
        confirmLabel: t('adminMarathons.syncConfirm'),
      })
    ) {
      syncTemplate.mutate(m.id);
    }
  };
  const askArchive = async (m: Marathon) => {
    if (await ask({ message: t('adminMarathons.archiveConfirm', { title: m.title }), danger: true })) {
      archive.mutate(m.id);
    }
  };

  const modals = (
    <>
      <MarathonFormModal
        open={creatingOpen}
        onClose={() => setCreatingOpen(false)}
        onCreated={() => qc.invalidateQueries({ queryKey: ['admin', 'marathons'] })}
      />

      {/*
        Ключ по образцу: диалог пересоздаётся под каждый образец, поэтому поля
        не тянут за собой название от предыдущего.
      */}
      {bindFor && (
        <BindTemplateDialog
          key={bindFor.id}
          marathon={bindFor}
          onClose={() => setBindFor(null)}
          onDone={() => {
            setBindFor(null);
            void list.refetch();
          }}
        />
      )}

      {fromTemplate && (
        <CreateFromTemplateModal
          key={fromTemplate.id}
          template={fromTemplate}
          onClose={() => setFromTemplate(null)}
          onCreated={() => {
            setFromTemplate(null);
            setTab('courses');
            qc.invalidateQueries({ queryKey: ['admin', 'marathons'] });
          }}
        />
      )}
    </>
  );

  // ── Телефон (docs/CURATOR-MOBILE-DESIGN.md §5.2) ──────────────────────────
  // Карточки с рядом из 4–6 значков без подписей → строки: обложка 48 px, название в две строки, «статус · уровни ·
  // язык · доступ», «учеников · уроков»; действия — листом «⋯» с подписями, состав по вкладке и статусу, как на
  // компьютере. Фильтры — фишками с окном выбора (правило 7). Нажатие строки — карточка курса.
  if (isMobile) {
    const setFilter = (patch: Partial<MarathonListParams>) => setFilters((f) => ({ ...f, ...patch, offset: 0 }));
    const sheetActions = (m: Marathon) =>
      tab === 'courses'
        ? [
            { label: t('adminMarathons.duplicate'), icon: 'content_copy', onSelect: () => duplicate.mutate(m.id) },
            ...(!m.source_template_id
              ? [{ label: t('adminMarathons.bindTemplate'), icon: 'link', onSelect: () => setBindFor(m) }]
              : []),
            { label: t('adminMarathons.makeTemplate'), icon: 'style', onSelect: () => void askMakeTemplate(m) },
            ...(m.status === 'draft'
              ? [{ label: t('adminMarathons.publish'), icon: 'publish', onSelect: () => publish.mutate(m.id) }]
              : []),
            ...(m.status === 'published'
              ? [{ label: t('adminMarathons.toArchive'), icon: 'archive', danger: true, onSelect: () => void askArchive(m) }]
              : []),
          ]
        : [
            { label: t('adminMarathons.createFromTemplate'), icon: 'add', onSelect: () => setFromTemplate(m) },
            { label: t('adminMarathons.sync'), icon: 'sync_alt', onSelect: () => void askSync(m) },
            { label: t('adminMarathons.unmakeTemplate'), icon: 'undo', onSelect: () => unmakeTemplate.mutate(m.id) },
          ];
    return (
      <div className="space-y-3">
        <MTitle
          title={t('adminMarathons.title')}
          subtitle={t('adminMarathons.subtitle')}
          right={<MAdd label={t('adminMarathons.createMarathon')} onClick={() => setCreatingOpen(true)} />}
        />
        <MSegmented
          tabs={[
            { key: 'courses', label: t('adminMarathons.tabCourses') },
            { key: 'templates', label: t('adminMarathons.tabTemplates') },
          ]}
          active={tab}
          onChange={(v) => {
            setTab(v);
            setFilters((f) => ({ ...f, offset: 0 }));
          }}
        />
        {actionError && <p className="px-0.5 text-[13px] text-error">{humanizeError(actionError)}</p>}
        <SearchInput
          placeholder={t('adminMarathons.searchPlaceholder')}
          value={filters.q ?? ''}
          onSearch={(q) => setFilter({ q: q || undefined })}
        />
        <div className="-mx-5 flex gap-1.5 overflow-x-auto px-5 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <MPickerChip
            label={t('marathon.filterAllStatuses')}
            value={filters.status ?? ''}
            options={STATUS_FILTER_OPTIONS}
            onChange={(v) => setFilter({ status: (v || undefined) as MarathonStatus | undefined })}
          />
          <MPickerChip
            label={t('marathon.filterAnyLanguage')}
            value={filters.language ?? ''}
            options={[
              { value: '', label: t('marathon.filterAnyLanguage') },
              { value: 'EN', label: t('marathon.langEN') },
              { value: 'RU', label: t('marathon.langRU') },
              { value: 'KZ', label: t('marathon.langKZ') },
            ]}
            onChange={(v) => setFilter({ language: (v || undefined) as MarathonListParams['language'] })}
          />
          <MPickerChip
            label={t('marathon.filterAnyLevel')}
            value={filters.level ?? ''}
            options={[
              { value: '', label: t('marathon.filterAnyLevel') },
              ...(['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const).map((l) => ({ value: l, label: l })),
            ]}
            onChange={(v) => setFilter({ level: (v || undefined) as CEFRLevel | undefined })}
          />
        </div>
        {list.isLoading ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.loading')}</p>
        ) : list.error ? (
          <p className="px-0.5 text-[13px] text-error">{humanizeError(list.error)}</p>
        ) : shown.length === 0 ? (
          <MEmpty
            icon="school"
            title={tab === 'templates' ? t('adminMarathons.noTemplates') : t('adminMarathons.nothingFound')}
          />
        ) : (
          <MBlock>
            {shown.map((m, i) => (
              <div
                key={m.id}
                data-admin-course-row
                className={cn('flex items-center gap-2 py-3', i < shown.length - 1 && 'border-b border-outline')}
              >
                <Link to={`/admin/marathons/${m.id}`} className="flex min-w-0 flex-1 items-center gap-3">
                  {m.cover_url ? (
                    <img src={m.cover_url} alt="" className="h-12 w-12 flex-none rounded-xl object-cover" />
                  ) : (
                    <MIcon icon="school" tone="muted" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="line-clamp-2 text-[14px] font-semibold leading-[1.3] text-on-surface">{m.title}</span>
                    <span className="block truncate text-[11.5px] text-on-surface-variant">
                      {[
                        statusLabel(m.status),
                        hasLevels(m.level_start, m.level_end) ? `${m.level_start || '—'} → ${m.level_end || '—'}` : '',
                        languageLabel(m.language),
                        accessLabel(m.access_mode),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                    <span className="block truncate text-[11.5px] text-on-surface-variant">
                      {t('curatorStudents.studentCount', { count: m.student_count ?? 0 })} ·{' '}
                      {t('staffMobile.lessonsN', { count: m.lesson_count ?? 0 })}
                      {m.is_template && <TemplateUsageText id={m.id} />}
                    </span>
                  </span>
                </Link>
                <button
                  type="button"
                  onClick={() => setSheetFor(m)}
                  aria-label={t('nav.more')}
                  className="grid h-11 w-11 flex-none place-items-center rounded-full text-on-surface-variant"
                >
                  <span className="material-symbols-outlined text-[20px]">more_horiz</span>
                </button>
              </div>
            ))}
          </MBlock>
        )}
        <Pagination
          offset={filters.offset ?? 0}
          limit={filters.limit ?? 20}
          total={list.data?.total ?? 0}
          onChange={(offset) => setFilters((f) => ({ ...f, offset }))}
        />
        <ActionSheet
          open={!!sheetFor}
          onClose={() => setSheetFor(null)}
          title={sheetFor?.title}
          actions={sheetFor ? sheetActions(sheetFor) : []}
        />
        {modals}
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={t('adminMarathons.title')}
        subtitle={t('adminMarathons.subtitle')}
        actions={
          <Button onClick={() => setCreatingOpen(true)}>
            <span className="material-symbols-outlined">add</span>
            {t('adminMarathons.createMarathon')}
          </Button>
        }
      />

      {/* Курсы и образцы — один список, две вкладки. */}
      <div className="mb-4 inline-flex rounded-button bg-surface-container-low p-1">
        {(['courses', 'templates'] as const).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => {
              setTab(v);
              setFilters((f) => ({ ...f, offset: 0 }));
            }}
            className={cn(
              'rounded-button px-4 py-2 text-sm font-semibold transition',
              tab === v ? 'bg-surface shadow-soft text-on-surface' : 'text-on-surface-variant',
            )}
          >
            {v === 'courses' ? t('adminMarathons.tabCourses') : t('adminMarathons.tabTemplates')}
          </button>
        ))}
      </div>

      {actionError && (
        <Card variant="subtle" className="mb-4 text-sm text-error">
          {humanizeError(actionError)}
        </Card>
      )}

      {/* Filters */}
      <Card variant="subtle" className="mb-6">
        <div className="grid gap-3 md:grid-cols-4">
          {/* Поиск по названию — на сервере, с задержкой: запрос не на каждую букву (разбор удобства 28.09). */}
          <SearchInput
            placeholder={t('adminMarathons.searchPlaceholder')}
            value={filters.q ?? ''}
            onSearch={(q) => setFilters((f) => ({ ...f, q: q || undefined, offset: 0 }))}
          />
          <Select
            options={STATUS_FILTER_OPTIONS}
            value={filters.status ?? ''}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                status: (e.target.value || undefined) as MarathonStatus | undefined,
                offset: 0,
              }))
            }
          />
          <Select
            options={[
              { value: '', label: t('marathon.filterAnyLanguage') },
              { value: 'EN', label: t('marathon.langEN') },
              { value: 'RU', label: t('marathon.langRU') },
              { value: 'KZ', label: t('marathon.langKZ') },
            ]}
            value={filters.language ?? ''}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                language: (e.target.value || undefined) as MarathonListParams['language'],
                offset: 0,
              }))
            }
          />
          <Select
            options={[
              { value: '', label: t('marathon.filterAnyLevel') },
              { value: 'A1', label: 'A1' },
              { value: 'A2', label: 'A2' },
              { value: 'B1', label: 'B1' },
              { value: 'B2', label: 'B2' },
              { value: 'C1', label: 'C1' },
              { value: 'C2', label: 'C2' },
            ]}
            value={filters.level ?? ''}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                level: (e.target.value || undefined) as CEFRLevel | undefined,
                offset: 0,
              }))
            }
          />
        </div>
      </Card>

      {/* Results */}
      {list.isLoading ? (
        <Card variant="subtle">{t('common.loading')}</Card>
      ) : list.error ? (
        <Card variant="subtle" className="text-error">
          {humanizeError(list.error)}
        </Card>
      ) : shown.length === 0 ? (
        <Card variant="subtle" className="text-center text-on-surface-variant">
          {/*
            На вкладке образцов не предлагаем «создать марафон»: он создался бы
            в соседней вкладке и выглядел бы пропавшим. Объясняем, как образец
            появляется на самом деле.
          */}
          {tab === 'templates' ? (
            t('adminMarathons.noTemplates')
          ) : (
            <>
              {t('adminMarathons.nothingFound')}{' '}
              <button
                type="button"
                onClick={() => setCreatingOpen(true)}
                className="font-semibold text-primary"
              >
                {t('adminMarathons.createFirstMarathon')}
              </button>
            </>
          )}
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {shown.map((m) => (
            <Card key={m.id} className="flex h-full flex-col">
              {m.cover_url && (
                <div
                  className="-mx-6 -mt-6 mb-4 h-32 rounded-t-card bg-cover bg-center"
                  style={{ backgroundImage: `url(${m.cover_url})` }}
                  aria-hidden
                />
              )}
              <Link to={`/admin/marathons/${m.id}`} className="block flex-1">
                <h3 className="font-display text-xl font-bold tracking-display text-on-surface">
                  {m.title}
                </h3>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Pill variant={m.status === 'published' ? 'success' : m.status === 'archived' ? 'neutral' : 'warning'}>
                    {statusLabel(m.status)}
                  </Pill>
                  {hasLevels(m.level_start, m.level_end) && (
                    <Pill variant="primary">
                      {m.level_start || '—'} → {m.level_end || '—'}
                    </Pill>
                  )}
                  <Pill variant="neutral">{languageLabel(m.language)}</Pill>
                  <Pill variant="neutral">{accessLabel(m.access_mode)}</Pill>
                  {m.is_template && <TemplateUsagePill id={m.id} />}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-xs text-on-surface-variant">
                  <span>
                    <strong className="block text-base text-on-surface">
                      {m.student_count ?? 0}
                    </strong>
                    {t('adminMarathons.students')}
                  </span>
                  <span>
                    <strong className="block text-base text-on-surface">
                      {m.lesson_count ?? 0}
                    </strong>
                    {t('adminMarathons.lessons')}
                  </span>
                </div>
              </Link>

              <div className="mt-4 flex flex-wrap gap-2 pt-3">
                <Link to={`/admin/marathons/${m.id}`}>
                  <Button size="sm" variant="secondary">
                    <span className="material-symbols-outlined text-base">construction</span>
                    {t('adminMarathons.constructor')}
                  </Button>
                </Link>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => duplicate.mutate(m.id)}
                  disabled={duplicate.isPending}
                  title={t('adminMarathons.duplicate')}
                >
                  <span className="material-symbols-outlined text-base">content_copy</span>
                </Button>

                {/*
                  Образцы. На вкладке марафонов — «сделать образцом», на вкладке
                  образцов — «создать марафон» и «вернуть в марафоны».
                */}
                {tab === 'courses' ? (
                  <>
                  {/* ⚠️ Привязка нужна курсам, заведённым КОПИРОВАНИЕМ: связи с
                      образцом у них нет, и правки образца до них не доходят.
                      Уже привязанному курсу кнопку не показываем — второй раз
                      привязать нельзя, и незачем. */}
                  {!m.source_template_id && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setBindFor(m)}
                      title={t('adminMarathons.bindTemplate')}
                    >
                      <span className="material-symbols-outlined text-base">link</span>
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={makeTemplate.isPending}
                    onClick={() => void askMakeTemplate(m)}
                    title={t('adminMarathons.makeTemplate')}
                  >
                    {/* style — иконка «стопка карточек»; bookmark в наш шрифт не входит */}
                    <span className="material-symbols-outlined text-base">style</span>
                  </Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" onClick={() => setFromTemplate(m)}>
                      <span className="material-symbols-outlined text-base">add</span>
                      {t('adminMarathons.createFromTemplate')}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      /* На кнопке коротко — иначе строка действий уезжает в три ряда. */
                      title={t('adminMarathons.sync')}
                      loading={syncTemplate.isPending && syncTemplate.variables === m.id}
                      onClick={() => void askSync(m)}
                    >
                      {/* sync_alt — в наш урезанный шрифт входит, просто sync нет */}
                      <span className="material-symbols-outlined text-base">sync_alt</span>
                      {t('adminMarathons.syncShort')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={unmakeTemplate.isPending}
                      onClick={() => unmakeTemplate.mutate(m.id)}
                      title={t('adminMarathons.unmakeTemplate')}
                    >
                      <span className="material-symbols-outlined text-base">undo</span>
                    </Button>
                  </>
                )}
                {/*
                  Публикация и архив — только у рабочих марафонов. Образец ученикам
                  не показывается ни в каком статусе, так что «опубликовать» для
                  него ничего не меняет: кнопка, которая ничего не делает, хуже
                  отсутствующей.
                */}
                {tab === 'courses' && m.status === 'draft' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => publish.mutate(m.id)}
                    disabled={publish.isPending}
                    title={t('adminMarathons.publish')}
                  >
                    <span className="material-symbols-outlined text-base">publish</span>
                  </Button>
                )}
                {tab === 'courses' && m.status === 'published' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => void askArchive(m)}
                    disabled={archive.isPending}
                    title={t('adminMarathons.toArchive')}
                  >
                    <span className="material-symbols-outlined text-base">archive</span>
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Листание (разбор удобства 28.09, №4): курсов больше 20, а `total` раньше не читался вовсе. */}
      <Pagination
        offset={filters.offset ?? 0}
        limit={filters.limit ?? 20}
        total={list.data?.total ?? 0}
        onChange={(offset) => setFilters((f) => ({ ...f, offset }))}
      />

      {modals}
    </>
  );
}

/**
 * Сколько марафонов создано из образца.
 *
 * Показываем прямо на карточке: перед правкой образца методист должен видеть,
 * что правка разойдётся не в пустоту, а по стольким-то живым марафонам.
 */
function TemplateUsagePill({ id }: { id: string }) {
  const { t } = useTranslation();
  const usage = useQuery({
    queryKey: ['admin', 'template-usage', id],
    queryFn: () => coursesApi.templateUsage(id),
  });
  // Пока считаем — молчим: мигающий ноль читался бы как «марафонов нет».
  if (usage.data === undefined) return null;
  return <Pill variant="primary">{t('adminMarathons.usedInCourses', { count: usage.data })}</Pill>;
}

/** То же число для строки на телефоне — хвостом «· используется в N» после «учеников · уроков». */
function TemplateUsageText({ id }: { id: string }) {
  const { t } = useTranslation();
  const usage = useQuery({
    queryKey: ['admin', 'template-usage', id],
    queryFn: () => coursesApi.templateUsage(id),
  });
  if (usage.data === undefined) return null;
  return <> · {t('adminMarathons.usedInCourses', { count: usage.data })}</>;
}

/**
 * Создание марафона из образца.
 *
 * Копия приходит черновиком: перед публикацией куратор меняет даты и название
 * группы. Адрес (slug) задаём руками — как и при обычном создании марафона; он
 * должен быть уникальным, и придумать его за человека мы не можем.
 */
function CreateFromTemplateModal({
  template,
  onClose,
  onCreated,
}: {
  template: Marathon;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(template.title);
  const [slug, setSlug] = useState('');

  const badSlug = slug.length > 0 && !/^[a-z0-9-]+$/.test(slug);
  const ready = title.trim().length > 0 && slug.length > 0 && !badSlug;

  const create = useMutation({
    mutationFn: () => coursesApi.createFromTemplate(template.id, title.trim(), slug),
    onSuccess: onCreated,
  });
  const isMobile = useIsMobile();
  // На телефоне — внизу окна во весь экран (правило 3); полоса вне <form>, отправка — по `form`.
  const buttons = (
    <div className={isMobile ? 'grid grid-cols-[auto_1fr] gap-2' : 'flex justify-end gap-2'}>
      <Button type="button" variant="secondary" onClick={onClose}>
        {t('common.cancel')}
      </Button>
      <Button type="submit" form="create-from-template" disabled={!ready} loading={create.isPending}>
        {t('adminMarathons.createFromTemplateSubmit')}
      </Button>
    </div>
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={t('adminMarathons.createFromTemplate')}
      phoneFull
      footer={isMobile ? buttons : undefined}
    >
      <form
        id="create-from-template"
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready && !create.isPending) create.mutate();
        }}
      >
        <p className="text-sm text-on-surface-variant">
          {t('adminMarathons.fromTemplateHint', { title: template.title })}
        </p>

        <Input
          label={t('marathonForm.labelTitle')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          autoFocus
        />
        <Input
          label={t('marathonForm.labelSlug')}
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="ielts-september"
          error={badSlug ? t('marathonForm.errorSlugPattern') : undefined}
        />

        {create.error && (
          <p className="text-sm text-error">{humanizeError(create.error)}</p>
        )}

        {!isMobile && buttons}
      </form>
    </Modal>
  );
}

/**
 * Привязка существующего курса к образцу.
 *
 * ⚠️ ЗАЧЕМ ОТДЕЛЬНОЕ ОКНО, А НЕ ПРОСТО КНОПКА. Связывание идёт по совпадению
 * места и названия, и при любом расхождении сервер отказывает целиком. Человеку
 * надо показать, ЧТО не совпало, — иначе он увидит красную строку и не поймёт,
 * какой из двадцати уроков виноват.
 */
function BindTemplateDialog({
  marathon,
  onClose,
  onDone,
}: {
  marathon: Marathon;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [picked, setPicked] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ lessons: number; blocks: number } | null>(null);

  // Образцы лежат в соседней вкладке — забираем их тем же запросом.
  const templates = useQuery({
    queryKey: ['admin', 'marathons', 'templates-for-bind'],
    queryFn: () => coursesApi.listAllMarathons({ template: 'true', limit: 100 }),
  });

  const bind = useMutation({
    mutationFn: () => coursesApi.bindTemplate(marathon.id, picked),
    onSuccess: (r) => setDone({ lessons: r.lessons, blocks: r.blocks }),
    onError: (e: unknown) => setError(humanizeError(e)),
  });
  const isMobile = useIsMobile();
  // На телефоне — внизу окна во весь экран (правило 3), на компьютере — под полем, как было.
  const buttons = done ? (
    <div className={isMobile ? 'grid' : 'mt-5 flex justify-end'}>
      <Button onClick={onDone}>{t('common.ok')}</Button>
    </div>
  ) : (
    <div className={isMobile ? 'grid grid-cols-[auto_1fr] gap-2' : 'mt-5 flex justify-end gap-2'}>
      <Button variant="secondary" onClick={onClose}>
        {t('common.cancel')}
      </Button>
      <Button disabled={!picked} loading={bind.isPending} onClick={() => bind.mutate()}>
        {t('adminMarathons.bindConfirm')}
      </Button>
    </div>
  );

  return (
    <Modal open onClose={onClose} title={t('adminMarathons.bindTitle')} phoneFull footer={isMobile ? buttons : undefined}>
      {done ? (
        <>
          <p className="text-sm text-on-surface">
            {t('adminMarathons.bindDone', { lessons: done.lessons, blocks: done.blocks })}
          </p>
          {!isMobile && buttons}
        </>
      ) : (
        <>
          <p className="text-sm text-on-surface-variant">
            {t('adminMarathons.bindHint', { title: marathon.title })}
          </p>

          <Select
            className="mt-4"
            label={t('adminMarathons.bindPick')}
            value={picked}
            onChange={(e) => {
              setPicked(e.target.value);
              setError(null);
            }}
            options={[
              { value: '', label: t('adminMarathons.bindPickEmpty') },
              ...(templates.data?.items ?? []).map((x) => ({ value: x.id, label: x.title })),
            ]}
          />

          {error && <p className="mt-3 text-sm text-error">{error}</p>}

          {!isMobile && buttons}
        </>
      )}
    </Modal>
  );
}
